/**
 * Hook 注册表
 * 管理内置 hooks 和用户自定义 hooks
 */

import { HookManager, HookType, HookEvent } from './hook-manager';
import { logger } from '../../utils/logger';
import { TaskStore } from '../task-store';
import { webhookService } from '../../services/webhook-service';
import path from 'path';
import fs from 'fs-extra';

export interface BuiltinHook {
  type: HookType | string;
  name: string;
  description: string;
  handler: (event: HookEvent) => Promise<void>;
  enabled?: boolean;
  priority?: number;
}

export class HookRegistry {
  private hookManager: HookManager;
  private taskStore: TaskStore;
  private builtinHooks: BuiltinHook[] = [];
  
  constructor(hookManager: HookManager, projectRoot?: string) {
    this.hookManager = hookManager;
    this.taskStore = new TaskStore(projectRoot);
    this.defineBuiltinHooks();
  }
  
  /**
   * 定义内置 hooks
   */
  private defineBuiltinHooks(): void {
    // 任务完成时同步到 Web
    this.builtinHooks.push({
      type: HookType.TASK_COMPLETE,
      name: 'sync-task-complete',
      description: '任务完成时同步到 Web 端',
      handler: async (event) => {
        const task = event.data;
        try {
          await webhookService.send({
            event: 'task.complete',
            data: { task },
            timestamp: new Date()
          });
          logger.debug('Task completion synced to web');
        } catch (error) {
          logger.error('Failed to sync task completion:', error);
        }
      },
      enabled: true,
      priority: 10
    });
    
    // 任务创建时生成 TODO.md
    this.builtinHooks.push({
      type: HookType.TASK_CREATE,
      name: 'update-todo-md',
      description: '任务创建时更新 TODO.md',
      handler: async (event) => {
        try {
          // TaskStore 会自动导出 TODO.md
          logger.debug('TODO.md will be updated by TaskStore');
        } catch (error) {
          logger.error('Failed to update TODO.md:', error);
        }
      },
      enabled: true,
      priority: 5
    });
    
    // PRD 创建时自动备份
    this.builtinHooks.push({
      type: HookType.PRD_CREATE,
      name: 'backup-prd',
      description: 'PRD 创建时自动备份',
      handler: async (event) => {
        const { path: prdPath } = event.data;
        try {
          const backupDir = path.join(path.dirname(prdPath), '.backup');
          await fs.ensureDir(backupDir);
          
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
          const backupPath = path.join(backupDir, `${path.basename(prdPath)}.${timestamp}`);
          
          await fs.copy(prdPath, backupPath);
          logger.debug(`PRD backed up to: ${backupPath}`);
        } catch (error) {
          logger.error('Failed to backup PRD:', error);
        }
      },
      enabled: true,
      priority: 5
    });
    
    // 设计文档审批时创建 Git 标签
    this.builtinHooks.push({
      type: HookType.DESIGN_APPROVE,
      name: 'create-git-tag',
      description: '设计文档审批时创建 Git 标签',
      handler: async (event) => {
        const { id } = event.data;
        try {
          // 这里可以调用 git 命令创建标签
          logger.info(`Design ${id} approved, consider creating a git tag`);
        } catch (error) {
          logger.error('Failed to create git tag:', error);
        }
      },
      enabled: false, // 默认禁用，需要用户启用
      priority: 5
    });
    
    // AI 思考时记录日志
    this.builtinHooks.push({
      type: HookType.AI_THINKING,
      name: 'log-ai-thinking',
      description: 'AI 思考过程记录',
      handler: async (event) => {
        const { prompt, context } = event.data;
        try {
          const logPath = path.join(process.cwd(), '.workflow/logs/ai-thinking.log');
          await fs.ensureDir(path.dirname(logPath));
          
          const logEntry = {
            timestamp: event.timestamp,
            prompt: prompt?.substring(0, 200) + '...',
            contextSize: JSON.stringify(context).length,
            source: event.source
          };
          
          await fs.appendFile(logPath, JSON.stringify(logEntry) + '\n');
        } catch (error) {
          logger.error('Failed to log AI thinking:', error);
        }
      },
      enabled: true,
      priority: 3
    });
    
    // 错误发生时发送通知
    this.builtinHooks.push({
      type: HookType.ERROR,
      name: 'error-notification',
      description: '错误发生时发送通知',
      handler: async (event) => {
        const { error, context } = event.data;
        logger.error('Hook system error:', error);
        // 这里可以集成通知服务
      },
      enabled: true,
      priority: 20
    });
  }
  
  /**
   * 注册所有内置 hooks
   */
  async registerBuiltinHooks(): Promise<void> {
    for (const hook of this.builtinHooks) {
      if (hook.enabled !== false) {
        this.hookManager.registerFunction(
          hook.type,
          hook.name,
          hook.handler,
          {
            priority: hook.priority
          }
        );
        logger.debug(`Registered builtin hook: ${hook.name}`);
      }
    }
  }
  
  /**
   * 扫描并注册用户 hooks
   */
  async scanUserHooks(hooksDir: string): Promise<void> {
    if (!await fs.pathExists(hooksDir)) {
      return;
    }
    
    const scanDir = async (dir: string, prefix: string = '') => {
      const entries = await fs.readdir(dir, { withFileTypes: true });
      
      for (const entry of entries) {
        const fullPath = path.join(dir, entry.name);
        
        if (entry.isDirectory()) {
          await scanDir(fullPath, prefix ? `${prefix}:${entry.name}` : entry.name);
        } else if (entry.isFile()) {
          const ext = path.extname(entry.name);
          if (['.sh', '.js', '.ts'].includes(ext)) {
            const hookType = this.inferHookType(prefix, entry.name);
            await this.hookManager.registerScript(hookType, fullPath, {
              name: entry.name,
              priority: 0
            });
            logger.debug(`Registered user hook: ${entry.name} for ${hookType}`);
          }
        }
      }
    };
    
    await scanDir(hooksDir);
  }
  
  /**
   * 推断 hook 类型
   */
  private inferHookType(category: string, filename: string): string {
    const name = path.basename(filename, path.extname(filename));
    
    // 尝试匹配预定义的 hook 类型
    for (const hookType of Object.values(HookType)) {
      const typeSegments = hookType.split(':');
      const categoryMatch = typeSegments[0] === category;
      const nameMatch = name.includes(typeSegments[1]);
      
      if (categoryMatch && nameMatch) {
        return hookType;
      }
    }
    
    // 返回自定义类型
    return `${category}:${name}`;
  }
  
  /**
   * 获取所有内置 hooks
   */
  getBuiltinHooks(): BuiltinHook[] {
    return this.builtinHooks;
  }
  
  /**
   * 启用/禁用内置 hook
   */
  async toggleBuiltinHook(hookName: string, enabled: boolean): Promise<void> {
    const hook = this.builtinHooks.find(h => h.name === hookName);
    if (hook) {
      hook.enabled = enabled;
      
      if (enabled) {
        this.hookManager.registerFunction(hook.type, hook.name, hook.handler, {
          priority: hook.priority
        });
      } else {
        await this.hookManager.toggleHook(hook.type, hook.name, false);
      }
      
      logger.info(`Builtin hook ${hookName} ${enabled ? 'enabled' : 'disabled'}`);
    }
  }
  
  /**
   * 创建 hook 模板
   */
  async createHookTemplate(hookType: string, scriptPath: string, language: 'bash' | 'javascript' = 'bash'): Promise<void> {
    let template = '';
    
    if (language === 'bash') {
      template = `#!/bin/bash
# Hook: ${hookType}
# Generated by Gongfu Workflow
#
# Available environment variables:
#   HOOK_TYPE    - The type of event (${hookType})
#   HOOK_DATA    - Event data in JSON format
#   HOOK_TIMESTAMP - ISO timestamp of the event
#   HOOK_SOURCE  - Source that triggered the event
#
# Example: Parse JSON data
# TASK_ID=$(echo "$HOOK_DATA" | jq -r '.id')
# TASK_CONTENT=$(echo "$HOOK_DATA" | jq -r '.content')

echo "[${hookType}] Hook triggered at $HOOK_TIMESTAMP"
echo "Data: $HOOK_DATA"

# Add your custom logic here
# ...

# Exit with 0 for success, non-zero for failure
exit 0
`;
    } else {
      template = `#!/usr/bin/env node
/**
 * Hook: ${hookType}
 * Generated by Gongfu Workflow
 */

const hookType = process.env.HOOK_TYPE;
const hookData = JSON.parse(process.env.HOOK_DATA || '{}');
const hookTimestamp = process.env.HOOK_TIMESTAMP;
const hookSource = process.env.HOOK_SOURCE;

console.log(\`[\${hookType}] Hook triggered at \${hookTimestamp}\`);
console.log('Data:', hookData);

// Add your custom logic here
async function main() {
  try {
    // Your code here
    
    process.exit(0);
  } catch (error) {
    console.error('Hook failed:', error);
    process.exit(1);
  }
}

main();
`;
    }
    
    await fs.ensureDir(path.dirname(scriptPath));
    await fs.writeFile(scriptPath, template);
    await fs.chmod(scriptPath, '755');
    
    logger.info(`Hook template created: ${scriptPath}`);
  }
}